// containers/EstimatesAdmin/EstimatesAdminList.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { listEstimates, createEstimate, deleteEstimate, deleteEstimatePdf, finalizeEstimate } from "../../actions/estimateActions";
import { useNavigate } from "react-router-dom";
import { generatePublicUrl } from "../../urlConfig";
import { saveAs } from "file-saver";
import { MainContainer } from "../MainContainer";
import { watchPresenceStats } from "../../actions/presence";
import AdminList from "../../components/AdminList/AdminList";
import ActionsMenu from "../../components/AdminList/ActionsMenu";
import PdfList from "../../components/AdminList/PdfList";

export default function EstimatesAdminList() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const userId = useSelector((s) => s.auth?.user?._id);
    const items = useSelector((s) => s.estimate?.list?.items) || [];
    const presenceStats = useSelector((s) => s.presence?.stats) || {};
    const total = useSelector((s) => s.estimate?.list?.total) || 0;
    const loading = useSelector((s) => s.estimate?.list?.loading);

    const [q, setQ] = useState("");
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => { dispatch(listEstimates({ q })); }, [q, dispatch]);

    useEffect(() => {
        const ids = items.map(d => d._id);
        const unsub = dispatch(watchPresenceStats({ roomIds: ids, prefix: "estimate:" }));
        return () => unsub && unsub();
    }, [items, dispatch]);

    const open = (id) => navigate(`/admin/estimates/${id}`);

    const createDraft = async () => {
        const payload = {
            creator: userId,
            title: "Смета",
            company: "ООО «Пример»",
            currency: "RUB",
            meta: { date: new Date().toISOString().slice(0, 10), markup: 10, hideMarkup: false, discount: 0 },
            rooms: [], materials: [], works: []
        };
        const doc = await dispatch(createEstimate(payload));
        if (doc?._id) open(doc._id);
    };

    const remove = async (id) => {
        if (!window.confirm("Удалить смету?")) return;
        await dispatch(deleteEstimate(id));
        dispatch(listEstimates({ q }));
    };

    const fmtDate = (s) => { try { return new Date(s).toLocaleDateString("ru-RU"); } catch { return "—"; } };

    const toAbsUrl = (raw) => {
        if (!raw) return null;
        if (/^https?:\/\//i.test(raw)) return raw;
        const rel = String(raw).replace(/^\/?public\//, "").replace(/^\//, "");
        return generatePublicUrl(rel);
    };

    const copyLink = async (url) => {
        try { await navigator.clipboard.writeText(url); }
        catch {
            const ta = document.createElement("textarea");
            ta.value = url; document.body.appendChild(ta);
            ta.select(); document.execCommand("copy"); ta.remove();
        }
    };

    const downloadUrl = async (url, filename = "document.pdf") => {
        try {
            const res = await fetch(url);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const blob = await res.blob();
            saveAs(blob, filename);
        } catch {
            window.open(url, "_blank", "noopener,noreferrer");
        }
    };

    const onDeletePdf = async (estimateId, fileRef) => {
        if (!fileRef) return;
        if (!window.confirm("Удалить запись о PDF?")) return;
        const alsoFile = window.confirm("Также удалить файл с сервера?");
        const ok = await dispatch(deleteEstimatePdf(estimateId, fileRef, alsoFile));
        if (ok?.ok) dispatch(listEstimates({ q }));
    };

    const onFinalize = async (docId) => {
        const resp = await dispatch(finalizeEstimate(docId, userId));
        if (resp?.url) {
            const abs = toAbsUrl(resp.url);
            if (abs) window.open(abs, "_blank", "noopener,noreferrer");
            dispatch(listEstimates({ q }));
        }
    };

    const lastPdfOf = useMemo(() => (doc) => (doc?.pdfs?.length ? doc.pdfs[doc.pdfs.length - 1] : null), []);
    const lastPdfUrlOf = (doc) => {
        const lp = lastPdfOf(doc);
        return lp ? toAbsUrl(lp.url || lp.filename) : null;
    };
    const lastPdfLabelOf = (doc) => {
        const lp = lastPdfOf(doc);
        return lp ? String(lp.filename || lp.url || "document.pdf").split("/").pop() : null;
    };

    const toolbar = {
        search: { value: q, onChange: setQ, placeholder: "Поиск по названию, компании…" },
        actions: [{ label: "+ Новая смета", onClick: createDraft, variant: "primary" }]
    };

    const columns = [
        { key: "num", title: "#", cell: (_d, i) => i + 1, headerClassName: "col col--num", className: "col col--num" },
        {
            key: "title",
            title: "Название",
            cell: (d) => (
                <div className="col--title">
                    <div className="row-title" title={d.title || "Смета"} onClick={() => open(d._id)}>{d.meta?.docnum || d.title || "Смета"}</div>
                    {d.templateName && <span className="badge badge--muted">template</span>}
                </div>
            )
        },
        { key: "company", title: "Компания", cell: (d) => <div className="muted">{d.company || "—"}</div> },
        { key: "date", title: "Обновлено", cell: (d) => <div title={d.updatedAt}>{fmtDate(d.updatedAt || d.meta?.date)}</div> },
        {
            key: "status",
            title: "Статус",
            cell: (d) => (
                <>
                    <span className={`badge ${d.status === "final" ? "badge--green" : d.isTemplate ? "badge--blue" : "badge--gray"}`}>{d.isTemplate ? "template" : (d.status || "draft")}</span>
                    <span className="badge" style={{ marginLeft: 8 }}>👥 {presenceStats[`estimate:${d._id}`]?.count || 0}</span>
                </>
            )
        },
        {
            key: "__actions",
            title: "Действия",
            className: "col col--actions",
            headerClassName: "col col--actions",
            cell: (d, _i, ctx) => (
                <ActionsMenu
                    items={[
                        { label: "Открыть редактор", onClick: () => open(d._id) },
                        { label: "Создать PDF", onClick: () => onFinalize(d._id) },
                        { type: "sep" },
                        { label: "Открыть последний PDF", onClick: () => { const u = lastPdfUrlOf(d); if (u) window.open(u, "_blank", "noopener,noreferrer"); }, disabled: !lastPdfUrlOf(d) },
                        { label: "Скачать последний PDF", onClick: () => { const u = lastPdfUrlOf(d); const n = lastPdfLabelOf(d); if (u) downloadUrl(u, n || "document.pdf"); }, disabled: !lastPdfUrlOf(d) },
                        { type: "sep" },
                        { label: ctx.isExpanded ? "Скрыть файлы" : "Показать все PDF", onClick: () => ctx.toggle() },
                        { type: "sep" },
                        { label: "Удалить", variant: "danger", onClick: () => remove(d._id) }
                    ]}
                />
            )
        }
    ];

    return (
        <MainContainer
            backButtonProduct
            type="minimalFunctional"
            href="/"
            title="Подготовка сметы"
            noneCreateButton
        >
            <AdminList
                toolbar={toolbar}
                columns={columns}
                items={items}
                loading={!!loading}
                emptyText="Документов нет"
                totalText={`Найдено: ${total}`}
                getRowKey={(d) => d._id}
                cols="64px 1.2fr 1fr 130px 120px 280px"
                colsMd="50px 1fr 1fr 120px 110px 280px"
                colsSm="1fr"
                renderSubrow={(d) => (
                    <PdfList
                        files={Array.isArray(d.pdfs) ? d.pdfs.slice().reverse() : []}
                        getLabel={(f) => String(f.filename || f.url || "document.pdf").split("/").pop()}
                        getUrl={(f) => toAbsUrl(f.url || f.filename)}
                        onOpen={(url) => window.open(url, "_blank", "noopener,noreferrer")}
                        onCopy={(url) => copyLink(url)}
                        onDownload={(url, _f, label) => downloadUrl(url, label)}
                        onDelete={(f) => onDeletePdf(d._id, f.filename || f.url)}
                    />
                )}
                theme="dark"
                expandedId={expandedId}
                onToggleExpand={(id) => setExpandedId((p) => (p === id ? null : id))}
            />
        </MainContainer>
    );
}
